import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  Button,
  Input,
  Tabs,
  Row,
  Col,
  Card,
  Badge,
  InputNumber,
  Divider,
  Space,
  Tag,
  Popconfirm,
  message,
  Tooltip,
  Switch,
  Select,
  Spin,
} from "antd";
import {
  DeleteOutlined,
  ShoppingCartOutlined,
  PercentageOutlined,
  DollarOutlined,
  MinusOutlined,
  PlusOutlined,
  SearchOutlined,
  QrcodeOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import moment from "moment";

import {
  getMenuItems,
  setSale,
} from "../../../Redux/actions/dailysalesActions";
import { getProduct } from "../../../Redux/actions/productActions";
import { setProductInventory } from "../../../Redux/actions/inventoryActions";
import { GetUsers } from "../../../Redux/actions/users";
import QRcomponent from "../../packages/PackageQRScanner/qrcomponent";

import "./styles.scss";

const currency = (n) =>
  `₱ ${
    Number.isFinite(n)
      ? n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      : "0.00"
  }`;

const PosSalesModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const { menuitems, locations, user, users } = useSelector(
    (s) => ({
      menuitems: s.dailysales.menuitems,
      locations: s.locations.locations,
      user: s.auth.user,
      users: s.users.users,
    }),
    shallowEqual,
  );

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeCat, setActiveCat] = useState("All");
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState({});
  const [orderDiscount, setOrderDiscount] = useState({
    dType: "pct",
    dValue: 0,
  });
  const [couponCode, setCouponCode] = useState("");
  const [orderNote, setOrderNote] = useState("");
  const [applyTax, setApplyTax] = useState(false);
  const [taxRate, setTaxRate] = useState(12);
  const [createDate, setCreateDate] = useState(moment());
  const [seller, setSeller] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [cameraID, setCameraID] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [scannerFrameHeight, setScannerFrameHeight] = useState(320);
  const scannerMode = activeCat === "__scan__";
  const lastScanRef = useRef({ text: "", at: 0 });
  const lastScanMessageRef = useRef({ text: "", at: 0 });

  const isConsignment = useMemo(() => {
    if (!locations || !user?.branch) return false;
    const dest = locations.find((i) => i.id == user.branch);
    return !!dest?.isConsigment;
  }, [locations, user]);

  const sameLocationUsers = useMemo(() => {
    const cobranch = users.filter((tao) => tao.branch == user.branch);
    // console.log("users", users);
    // console.log("user", user);
    // console.log("cobranch", cobranch);
    return cobranch;
  }, [users, user]);

  useEffect(() => {
    if (open) {
      dispatch(getMenuItems());
      dispatch(GetUsers());
    }
  }, [open, dispatch]);

  useEffect(() => {
    const mediaDevices =
      typeof navigator !== "undefined" ? navigator.mediaDevices : null;
    const enumerateDevices = mediaDevices?.enumerateDevices;

    if (!open || !scannerMode || typeof enumerateDevices !== "function") {
      setCameras([]);
      setCameraID(null);
      return;
    }

    enumerateDevices
      .call(mediaDevices)
      .then((devices) => {
        const videoInputs = devices.filter(
          (device) => device.kind === "videoinput",
        );
        setCameras(videoInputs);
        if (!cameraID && videoInputs.length > 0) {
          setCameraID(videoInputs[0].deviceId);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }, [open, scannerMode, cameraID]);

  useEffect(() => {
    if (!open || !scannerMode || typeof window === "undefined") {
      return undefined;
    }

    const updateScannerFrameHeight = () => {
      const viewportHeight = window.innerHeight || 800;
      const nextHeight = Math.max(
        260,
        Math.min(Math.floor(viewportHeight * 0.42), 420),
      );
      setScannerFrameHeight(nextHeight);
    };

    updateScannerFrameHeight();
    window.addEventListener("resize", updateScannerFrameHeight);

    return () => {
      window.removeEventListener("resize", updateScannerFrameHeight);
    };
  }, [open, scannerMode]);

  useEffect(() => {
    if (!scanLoading) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setScanLoading(false);
    }, 1200);

    return () => clearTimeout(timer);
  }, [scanLoading]);

  useEffect(() => {
    console.log(seller);
  }, [seller]);

  const categories = useMemo(() => {
    const cats = new Set();
    (menuitems ?? []).forEach((p) =>
      cats.add(p.category_name || "Uncategorized"),
    );
    return ["All", ...Array.from(cats)];
  }, [menuitems]);

  const filteredProducts = useMemo(() => {
    const list = (menuitems ?? [])
      .filter((p) => p.menu_item || p.is_resell)
      .filter((p) =>
        activeCat === "All" ? true : p.category_name === activeCat,
      )
      .filter((p) => p.name.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => a.name.localeCompare(b.name));

    const unique = [];
    const seen = new Set();
    for (const p of list) {
      if (!seen.has(p.id)) {
        seen.add(p.id);
        unique.push(p);
      }
    }
    return unique;
  }, [menuitems, activeCat, search]);

  const lineSubtotal = (ln) => ln.qty * ln.price;
  const lineDiscount = (ln) =>
    ln.dType === "pct"
      ? (lineSubtotal(ln) * Math.min(Math.max(ln.dValue, 0), 100)) / 100
      : Math.min(Math.max(ln.dValue, 0), lineSubtotal(ln));
  const lineTotal = (ln) => Math.max(0, lineSubtotal(ln) - lineDiscount(ln));

  const cartArray = useMemo(() => Object.values(cart), [cart]);

  const totals = useMemo(() => {
    const subtotal = cartArray.reduce((s, ln) => s + lineSubtotal(ln), 0);
    const itemsDiscount = cartArray.reduce((s, ln) => s + lineDiscount(ln), 0);
    const afterItems = Math.max(0, subtotal - itemsDiscount);
    const orderDisc =
      orderDiscount.dType === "pct"
        ? (afterItems * Math.min(Math.max(orderDiscount.dValue, 0), 100)) / 100
        : Math.min(Math.max(orderDiscount.dValue, 0), afterItems);
    const discounted = Math.max(0, afterItems - orderDisc);
    const tax = (discounted * Math.max(taxRate, 0)) / 100;
    const payable = discounted + (applyTax ? tax : 0);
    return { subtotal, itemsDiscount, orderDisc, tax, payable };
  }, [cartArray, orderDiscount, applyTax, taxRate]);

  const paymentOptions = useMemo(
    () => [
      { value: "cash", label: "Cash Sales" },
      { value: "online", label: "Online Sales" },
      { value: "qrph", label: "QRPH" },
      { value: "card", label: "Card Payment" },
      { value: "amex", label: "AMEX Payment" },
    ],
    [],
  );

  const addToCart = (p) => {
    const unitPrice =
      isConsignment && p?.consigment_price
        ? Number(p.consigment_price)
        : Number(p.sell_price || 0);
    // if (!unitPrice) {
    //   message.warning(`${p.name} has no price set`);
    //   return;
    // }
    setCart((prev) => {
      const ex = prev[p.id];
      const nextQty = (ex?.qty ?? 0) + 1;
      return {
        ...prev,
        [p.id]: ex
          ? { ...ex, qty: nextQty }
          : {
              id: p.id,
              name: p.name,
              qty: 1,
              price: unitPrice ? unitPrice : 0,
              dType: "pct",
              dValue: 0,
              imageUrl: `${process.env.REACT_APP_AWS_S3}${p.id}.png`,
              raw: p,
            },
      };
    });
  };

  const handleProductScan = (event) => {
    if (!event?.text || scanLoading) {
      return;
    }

    const text = event.text.trim();
    const now = Date.now();
    const lastScan = lastScanRef.current;
    const lastMessage = lastScanMessageRef.current;

    if (!text.startsWith("prod_")) {
      if (lastMessage.text !== text || now - lastMessage.at > 1000) {
        lastScanMessageRef.current = { text, at: now };
        message.warning(
          "Invalid product code. Expected a code starting with prod_.",
        );
      }
      return;
    }

    if (lastScan.text === text && now - lastScan.at < 1000) {
      return;
    }

    const productId = text.slice(5);

    const scannedProduct = (menuitems ?? [])
      .filter((p) => p.menu_item || p.is_resell)
      .find((p) => p.id === productId);

    if (!scannedProduct) {
      if (lastMessage.text !== text || now - lastMessage.at > 1000) {
        lastScanMessageRef.current = { text, at: now };
        message.warning("Scanned product is not available in this sales list.");
      }
      return;
    }

    lastScanRef.current = { text, at: now };
    lastScanMessageRef.current = { text, at: now };
    setScanLoading(true);
    addToCart(scannedProduct);
    message.success(`${scannedProduct.name} added`);
  };

  const updateLine = (id, patch) =>
    setCart((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const removeLine = (id) =>
    setCart((prev) => {
      const n = { ...prev };
      delete n[id];
      return n;
    });

  const resetAll = () => {
    setCart({});
    setOrderDiscount({ dType: "pct", dValue: 0 });
    setCouponCode("");
    setOrderNote("");
    setApplyTax(false);
    setTaxRate(12);
    setCreateDate(moment());
    setSeller(null);
    setPaymentMethod(null);
  };

  const summarizeAndSave = async () => {
    if (!cartArray.length) {
      message.warning("Select at least one item.");
      return;
    }

    const products_sold = cartArray.map((ln) => ({
      ...ln.raw,
      quantity_sold: ln.qty,
      unit_price_used: ln.price,
      item_discount_type: ln.dType,
      item_discount_value: ln.dValue,
      line_subtotal: lineSubtotal(ln),
      line_discount: lineDiscount(ln),
      line_total: lineTotal(ln),
      qty_reg: ln.qty,
    }));

    const ingredientCounts = {};
    for (const prod of products_sold) {
      const q = Number(prod.quantity_sold || 0);
      if (Array.isArray(prod.ingredients)) {
        for (const ing of prod.ingredients) {
          const key = String(ing.product);
          const addQty = Number(ing.quantity) * q;
          if (!ingredientCounts[key]) {
            ingredientCounts[key] = {
              product: ing.product,
              product_name: ing.product_name,
              quantity: 0,
            };
          }
          ingredientCounts[key].quantity += addQty;
        }
      }
    }
    const ingredient_final = Object.values(ingredientCounts);
    const sell_total = totals.payable;

    setLoading(true);

    try {
      // 1) Deduct ingredients usage (per-item guarded)
      for (let i = 0; i < ingredient_final.length; i++) {
        const ing = ingredient_final[i];
        try {
          await dispatch(
            setProductInventory(
              ing.product,
              ing.product_name,
              ing.quantity,
              "subtract",
              "used",
            ),
          );
          await dispatch(getProduct(ing.product));
        } catch (e) {
          // log & continue sa next ingredient
          console.error("Ingredient update failed:", ing, e);
          message.error(
            `Failed to update ingredient: ${ing?.product_name ?? ing?.product}`,
          );
        }
      }
    } catch (e) {
      // fallback guard (shouldn't usually hit dahil per-item try/catch na)
      console.error("Ingredients loop failed:", e);
      message.error("Failed while processing ingredients.");
    }

    try {
      // 2) Deduct inventory for resell products (per-item guarded)
      for (const prod of products_sold) {
        if (!prod?.is_resell) continue;
        try {
          await dispatch(
            setProductInventory(
              prod.id,
              prod.name,
              prod.quantity_sold,
              "subtract",
              "sold",
            ),
          );
        } catch (e) {
          console.error("Resell product inventory update failed:", prod, e);
          message.error(
            `Failed to update inventory for: ${prod?.name ?? prod?.id}`,
          );
        }
      }
    } catch (e) {
      console.error("Products_sold loop failed:", e);
      message.error("Failed while processing sold products.");
    }

    try {
      // 3) Create the sale record
      const userSeller = sameLocationUsers.find((peeps) => seller == peeps.id);

      await dispatch(
        setSale(products_sold, ingredient_final, createDate, sell_total, {
          order_discount_type: orderDiscount.dType,
          order_discount_value: orderDiscount.dValue,
          coupon_code: couponCode || undefined,
          note: orderNote || undefined,
          applied_tax: applyTax ? taxRate : 0,
          breakdown: totals,
          seller: userSeller,
          payment_method: paymentMethod ?? null,
        }),
      );
    } catch (e) {
      console.error("setSale failed:", e);
      message.error("Failed to save sale");
    }

    try {
      // 4) UI feedback & cleanup (last step)
      message.success("Sale recorded");
      resetAll();
      onClose("reset");
    } catch (e) {
      console.warn("Post-save UI cleanup failed:", e);
      message.warning("Sale saved, pero may problema sa UI reset.");
    } finally {
      // ALWAYS at the bottom
      setLoading(false);
    }
  };

  return (
    <>
      <div className="addeditbutton">
        <Button
          type="primary"
          shape="round"
          icon={<PlusOutlined />}
          onClick={() => {
            setOpen(true);
            setLoading(false);
          }}
        >
          Add Sale
        </Button>
      </div>

      <Modal
        open={open}
        onCancel={() => {
          if (loading) {
            return;
          }
          onClose();
          setOpen(false);
        }}
        title={
          <div className="pos__header">
            <div>
              <span>Add Sales</span>
            </div>

            <div>
              <span style={{ flex: 1 }} />
              <span>Date:</span>
              <input
                type="date"
                value={createDate.format("YYYY-MM-DD")}
                max={moment().format("YYYY-MM-DD")}
                onChange={(e) => setCreateDate(moment(e.target.value))}
              />
              <Tag color={isConsignment ? "gold" : "blue"}>
                {isConsignment ? "Consignment" : "Regular"}
              </Tag>
            </div>
            <div></div>
          </div>
        }
        width="100vw"
        style={{ top: 0, bottom: 0 }}
        className="pos-modal" // styles the dialog
        rootClassName="pos-modal-root"
        closable={!loading}
        maskClosable={!loading}
        footer={
          <Space wrap>
            <Select
              style={{ minWidth: "22ch" }}
              placeholder="select payment method"
              value={paymentMethod ?? undefined}
              onChange={(v) => setPaymentMethod(v)}
              options={paymentOptions}
              allowClear
            />
            {sameLocationUsers.length > 0 ? (
              <Select
                onChange={(e) => {
                  setSeller(e);
                }}
                style={{ minWidth: "20ch" }}
                placeholder="select seller"
              >
                {sameLocationUsers.map((tao) => (
                  <Select.Option value={tao.id}>{tao.name}</Select.Option>
                ))}
              </Select>
            ) : null}

            {/* <Button>Hold Cart</Button> */}

            <Popconfirm
              title="Confirm checkout?"
              // onConfirm={summarizeAndSave}
              onConfirm={() => {
                summarizeAndSave();
              }}
              okText="Proceed"
              disabled={seller == null}
            >
              <Button
                type="primary"
                icon={<ShoppingCartOutlined />}
                loading={loading}
                onClick={() => {
                  if (seller == null) {
                    message.warn("please select seller first");
                  }
                }}
              >
                Proceed
              </Button>
            </Popconfirm>
          </Space>
        }
      >
        {loading ? (
          <div className="pos-processing-overlay">
            <div className="pos-processing-box">
              <Spin size="large" />
              <span>Recording sale. Please wait...</span>
            </div>
          </div>
        ) : null}
        <div className="pos">
          <Row gutter={12} wrap>
            {/* LEFT: Catalog (independent vertical scroll) */}
            <Col xs={24} md={14} className="pos__catalog">
              <div className="pos__search">
                <Input
                  allowClear
                  prefix={<SearchOutlined />}
                  placeholder="Search products..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  size="large"
                />
              </div>

              <Tabs
                activeKey={activeCat}
                onChange={setActiveCat}
                items={[
                  {
                    key: "__scan__",
                    label: (
                      <span>
                        <QrcodeOutlined /> Scan
                      </span>
                    ),
                  },
                  ...categories.map((c) => ({ key: c, label: c })),
                ]}
                tabBarGutter={8}
                animated
                className="pos__catalog-tabs-list"
              />

              <div
                className={`pos__catalog-scroll ${scannerMode ? "pos__catalog-scroll--scanner" : ""}`}
              >
                {scannerMode ? (
                  <div className="pos__scanner pos__scanner--full">
                    {cameras.length > 1 ? (
                      <Select
                        value={cameraID ?? undefined}
                        onChange={setCameraID}
                        placeholder="Select camera"
                        className="pos__scanner-select"
                        options={cameras.map((cam, index) => ({
                          value: cam.deviceId,
                          label: cam.label || `camera ${index + 1}`,
                        }))}
                      />
                    ) : null}
                    <div
                      className="pos__scanner-frame"
                      style={{ height: `${scannerFrameHeight}px` }}
                    >
                      <QRcomponent
                        cameraID={cameraID}
                        onScan={(e) => {
                          if (e) {
                            handleProductScan(e);
                          }
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="pos__grid">
                    {filteredProducts.map((p) => {
                      const cardPrice =
                        isConsignment && p?.consigment_price
                          ? Number(p.consigment_price)
                          : Number(p.sell_price || 0);
                      return (
                        <Card
                          key={p.id}
                          hoverable
                          className="pos__card"
                          bodyStyle={{ padding: 10 }}
                          onClick={() => addToCart(p)}
                          cover={
                            <div className="pos__card-cover">
                              <img
                                src={`${process.env.REACT_APP_AWS_S3}${p.id}.png`}
                                alt={p.name}
                                onError={(e) => {
                                  e.target.src = `${process.env.REACT_APP_AWS_S3}logo192.png`;
                                }}
                              />
                            </div>
                          }
                        >
                          <div className="pos__card-title">{p.name}</div>
                          <div className="pos__price">
                            {currency(cardPrice)}
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            </Col>

            {/* RIGHT: Cart (independent vertical scroll) */}
            <Col xs={24} md={10} className="pos__cart">
              <div className="pos__cart-title">
                <div>
                  Cart <Badge count={cartArray.length} />
                </div>

                <Popconfirm
                  title="Confirm Reset?"
                  onConfirm={resetAll}
                  okText="Proceed"
                >
                  <Button
                    type="dashed"
                    icon={<DeleteOutlined />}
                    loading={loading}
                  >
                    Reset
                  </Button>
                </Popconfirm>
              </div>

              <div className="pos__cart-scroll">
                {cartArray.length === 0 && (
                  <div className="pos__empty">No items yet.</div>
                )}

                {cartArray.map((ln) => (
                  <Card key={ln.id} size="small" className="pos__line">
                    {/* Compact row */}
                    <div className="pos__line-top">
                      <img
                        className="pos__line-img"
                        src={ln.imageUrl}
                        alt={ln.name}
                        onError={(e) => {
                          e.target.src = `${process.env.REACT_APP_AWS_S3}logo192.png`;
                        }}
                      />
                      <div className="pos__line-main">
                        <div className="pos__line-title">{ln.name}</div>
                        <div className="pos__meta">
                          {currency(ln.price)} / item
                        </div>
                      </div>

                      <div className="pos__qty">
                        <Space.Compact>
                          <Button
                            size="small"
                            icon={<MinusOutlined />}
                            onClick={() =>
                              updateLine(ln.id, {
                                qty: Math.max(0, ln.qty - 1),
                              })
                            }
                          />
                          <InputNumber
                            size="small"
                            min={0}
                            value={ln.qty}
                            onChange={(v) =>
                              updateLine(ln.id, { qty: Number(v || 0) })
                            }
                            style={{ width: "6ch" }}
                          />
                          <Button
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() =>
                              updateLine(ln.id, { qty: ln.qty + 1 })
                            }
                          />
                        </Space.Compact>
                      </div>

                      <div className="pos__line-total">
                        {currency(lineTotal(ln))}
                      </div>

                      {/* more toggle */}
                      <Button
                        size="small"
                        className="pos__more-btn"
                        onClick={() => updateLine(ln.id, { open: !ln.open })}
                      >
                        ⋯
                      </Button>
                    </div>

                    {/* Extra panel BELOW the row (full width) */}
                    {ln.open && (
                      <div className="pos__line-extra">
                        <Space wrap size={8}>
                          <Tag
                            className="pos__chip"
                            icon={
                              ln.dType === "pct" ? (
                                <PercentageOutlined />
                              ) : (
                                <DollarOutlined />
                              )
                            }
                          >
                            {ln.dType === "pct" ? "%" : "₱"}
                          </Tag>
                          <Switch
                            size="small"
                            checkedChildren="%"
                            unCheckedChildren="₱"
                            checked={ln.dType === "pct"}
                            onChange={(checked) =>
                              updateLine(ln.id, {
                                dType: checked ? "pct" : "amt",
                                dValue: 0,
                              })
                            }
                          />
                          <InputNumber
                            size="small"
                            min={0}
                            max={ln.dType === "pct" ? 100 : undefined}
                            value={ln.dValue}
                            onChange={(v) =>
                              updateLine(ln.id, { dValue: Number(v || 0) })
                            }
                            addonAfter={ln.dType === "pct" ? "%" : "₱"}
                            style={{ width: 92 }}
                          />
                        </Space>
                        <div className="pos__line-subs">
                          Subtotal: {currency(lineSubtotal(ln))} · Discount:{" "}
                          {currency(lineDiscount(ln))}
                        </div>

                        <Tooltip title="Remove">
                          <Button
                            size="small"
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => removeLine(ln.id)}
                          />
                        </Tooltip>
                      </div>
                    )}
                  </Card>
                ))}
              </div>

              {/* Controls under cart list */}
              <div className="pos__tabs-wrap">
                <Tabs
                  defaultActiveKey="add"
                  items={[
                    { key: "add", label: "Add", children: <div /> },
                    {
                      key: "discount",
                      label: "Discount",
                      children: (
                        <Space
                          direction="vertical"
                          style={{ width: "100%" }}
                          size={8}
                        >
                          <Space size={8} wrap>
                            <Tag
                              className="pos__chip"
                              icon={<PercentageOutlined />}
                            >
                              Order Discount
                            </Tag>
                            <Switch
                              size="small"
                              checkedChildren="%"
                              unCheckedChildren="₱"
                              checked={orderDiscount.dType === "pct"}
                              onChange={(checked) =>
                                setOrderDiscount({
                                  dType: checked ? "pct" : "amt",
                                  dValue: 0,
                                })
                              }
                            />
                            <InputNumber
                              size="small"
                              min={0}
                              max={
                                orderDiscount.dType === "pct" ? 100 : undefined
                              }
                              value={orderDiscount.dValue}
                              onChange={(v) =>
                                setOrderDiscount((d) => ({
                                  ...d,
                                  dValue: Number(v || 0),
                                }))
                              }
                              addonAfter={
                                orderDiscount.dType === "pct" ? "%" : "₱"
                              }
                              style={{ width: 92 }}
                            />
                          </Space>
                          <Space size={8} wrap>
                            <Tag className="pos__chip">Tax</Tag>
                            <Switch
                              size="small"
                              checked={applyTax}
                              onChange={setApplyTax}
                            />
                            <InputNumber
                              size="small"
                              min={0}
                              max={100}
                              disabled={!applyTax}
                              value={taxRate}
                              onChange={(v) => setTaxRate(Number(v || 0))}
                              addonAfter="%"
                              style={{ width: 92 }}
                            />
                          </Space>
                        </Space>
                      ),
                    },
                    // {
                    //   key: "coupon",
                    //   label: "Coupon Code",
                    //   children: (
                    //     <Input
                    //       placeholder="Enter coupon/promo code"
                    //       value={couponCode}
                    //       onChange={(e) => setCouponCode(e.target.value)}
                    //       size="middle"
                    //     />
                    //   ),
                    // },
                    {
                      key: "note",
                      label: "Note",
                      children: (
                        <Input.TextArea
                          rows={2}
                          placeholder="Order note / special instructions"
                          value={orderNote}
                          onChange={(e) => setOrderNote(e.target.value)}
                        />
                      ),
                    },
                  ]}
                />
              </div>

              {/* Sticky compact totals */}
              <div className="pos__totals">
                <div className="totals-grid">
                  <div className="label">Subtotal</div>
                  <div className="value strong">
                    {currency(totals.subtotal)}
                  </div>
                  <div className="label muted">Item Discounts</div>
                  <div className="value muted">
                    - {currency(totals.itemsDiscount)}
                  </div>
                  <div className="label muted">Order Discount</div>
                  <div className="value muted">
                    - {currency(totals.orderDisc)}
                  </div>
                  <div className="label muted">
                    Tax {applyTax ? `(${taxRate}%)` : ""}
                  </div>
                  <div className="value muted">{currency(totals.tax)}</div>
                </div>
                <div className="payable-row">
                  <span>Payable Amount</span>
                  <span className="amount">{currency(totals.payable)}</span>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </Modal>
    </>
  );
};

export default PosSalesModal;
